import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { LocationFilter } from './types';
import { useGoogleMapsApi } from '../../GoogleMapsApiContext';
import './SearchHeader.scss';

interface SearchHeaderProps {
  onLocationChange: (location: LocationFilter | null) => void;
  showFilters: boolean;
  onToggleFilters: () => void;
  setFocusToLocationInput?: boolean;
}

const SearchHeader: React.FC<SearchHeaderProps> = ({
  onLocationChange,
  showFilters,
  onToggleFilters,
  setFocusToLocationInput = false
}) => {
  const { t } = useTranslation();
  const [address, setAddress] = useState('');
  const { isLoaded, loadError } = useGoogleMapsApi();
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the input when component mounts and setFocusToLocationInput is true
  useEffect(() => {
    if (setFocusToLocationInput && inputRef.current) {
      // Small delay to ensure the component is fully rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [setFocusToLocationInput]);

  const handleAddressChange = (address: string) => {
    setAddress(address);
  };

  const handleAddressSelect = useCallback(async (address: string) => {
    try {
      const results = await geocodeByAddress(address);
      if (results.length === 0) return;

      const latLng = await getLatLng(results[0]);

      // Check if this is a neighborhood by looking at the main level types
      const isNeighborhood = results[0].types?.includes('neighborhood') &&
        results[0].types?.includes('political');

      // Extract structured address components
      const components: LocationFilter['components'] = {};

      for (const component of results[0].address_components) {
        const types = component.types;

        if (types.includes('postal_code')) {
          components.postalCode = component.long_name;
        } else if (types.includes('locality')) {
          components.city = component.long_name;
        } else if (types.includes('country')) {
          components.country = component.long_name;
        } else if (types.includes('route')) {
          components.street = component.long_name;
        } else if (
          types.includes('neighborhood')
          || types.includes('sublocality')
          || types.includes('natural_feature')
        ) {
          components.neighborhood = component.long_name;
        } else if (types.includes('establishment') && !components.neighborhood) {
          components.neighborhood = component.long_name;
        }
      }

      // If this is a neighborhood search but we didn't get neighborhood from address_components,
      // extract it from the first part of the formatted address
      if (isNeighborhood && !components.neighborhood) {
        const addressParts = results[0].formatted_address.split(',');
        if (addressParts.length > 0) {
          components.neighborhood = addressParts[0].trim();
        }
      }

      // Send both the formatted address and structured components
      onLocationChange({
        address,
        components,
        center: latLng
      });

      setAddress(address);
    } catch (error) {
      console.error('Error selecting address:', error);
    }
  }, [onLocationChange]);

  const handleClearLocation = useCallback(() => {
    setAddress('');
    onLocationChange(null);
  }, [onLocationChange]);

  if (loadError) {
    return <div className="search-header__error">{t('exploreListings.mapLoadError')}</div>;
  }

  if (!isLoaded) {
    return <div className="search-header__loading">{t('exploreListings.mapLoading')}</div>;
  }

  return (
    <div className="search-header">
      <div className="search-header__location-search">
        <PlacesAutocomplete
          value={address}
          onChange={handleAddressChange}
          onSelect={handleAddressSelect}
        >
          {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
            <div className="search-header__autocomplete">
              <div className="search-header__input-wrapper">
                <svg className="search-header__search-icon" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 0c7.18 0 13 5.82 13 13 0 2.868-.929 5.519-2.502 7.669l7.916 7.917-2.828 2.828-7.917-7.916A12.942 12.942 0 0 1 13 26C5.82 26 0 20.18 0 13S5.82 0 13 0zm0 4a9 9 0 1 0 0 18 9 9 0 0 0 0-18z" fill="currentColor"></path>
                </svg>
                <input
                  {...getInputProps({
                    placeholder: t('exploreListings.searchPlaceholder'),
                    className: `search-header__input ${address ? '' : 'search-header__input--no-address-set'}`,
                    ref: inputRef
                  })}
                />
                {address && (
                  <button
                    className="search-header__clear-button"
                    onClick={handleClearLocation}
                    type="button"
                  >
                    <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                      <path d="m6 6 20 20M26 6 6 26" stroke="currentColor" strokeWidth="3"></path>
                    </svg>
                  </button>
                )}
              </div>
              {suggestions.length > 0 && (
                <div className="search-header__suggestions">
                  {loading && (
                    <div className="search-header__suggestion search-header__suggestion--loading">
                      {t('exploreListings.loading')}
                    </div>
                  )}
                  {suggestions.map((suggestion) => (
                    <div
                      {...getSuggestionItemProps(suggestion, {
                        className: `search-header__suggestion ${suggestion.active ? 'search-header__suggestion--active' : ''
                          }`
                      })}
                      key={suggestion.placeId}
                    >
                      <svg className="search-header__suggestion-icon" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 0a5 5 0 0 1 5 5c0 1.283-.502 2.445-1.319 3.306l-.024.024L8 14 4.343 8.33l-.024-.024A4.98 4.98 0 0 1 3 5a5 5 0 0 1 5-5z"></path>
                      </svg>
                      <span>{suggestion.description}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </PlacesAutocomplete>
      </div>

      <button
        className={`search-header__filters-button ${showFilters ? 'search-header__filters-button--active' : ''}`}
        onClick={onToggleFilters}
      >
        <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
          <path d="M5 8a3 3 0 0 1 2.83 2H14v2H7.83A3 3 0 1 1 5 8zm0 2a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm6-8a3 3 0 1 1 0 6 3 3 0 0 1 0-6zm0 2a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"></path>
        </svg>
        <span>{t('exploreListings.filters')}</span>
      </button>
    </div>
  );
};

export default SearchHeader; 